import { useState, useEffect } from 'react';
import { frontendDb as db } from '../lib/frontend-db';
import { X } from 'lucide-react';
import type { NewsItem } from '../lib/frontend-db';

const NewsSection = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [selectedArticle, setSelectedArticle] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(6);
  const [sectionTitle, setSectionTitle] = useState('Aktuelles');

  useEffect(() => {
    loadNews();
    loadSettings();
  }, []);

  const loadNews = async () => {
    try {
      const newsItems = await db.getNews();
      setNews(newsItems);
    } catch (error) {
      console.error('Error loading news:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const settings = await db.getSettings();
      if (settings) {
        setSectionTitle((settings as any).news_title || 'Aktuelles');
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const displayedNews = news.slice(0, visibleCount);
  const itemCount = displayedNews.length;
  const isSingle = itemCount === 1;

  const computeReadingTimeMinutes = (text: string): number => {
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    const minutes = Math.round(words / 200);
    return Math.max(1, minutes);
  };

  const parseMySqlTimestampToDate = (value: string | Date | null | undefined): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return value;
    // Match formats like "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DD"
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?$/);
    if (match) {
      const [, y, m, d, hh = '00', mm = '00', ss = '00'] = match;
      const dateUtc = new Date(Date.UTC(
        Number(y),
        Number(m) - 1,
        Number(d),
        Number(hh),
        Number(mm),
        Number(ss)
      ));
      return isNaN(dateUtc.getTime()) ? null : dateUtc;
    }
    const fallback = new Date(value);
    return isNaN(fallback.getTime()) ? null : fallback;
  };

  if (loading) {
    return (
      <section id="news" className="py-16 dark:bg-slate-800 transition-colors">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[var(--primary-color)] mb-8 text-center">{sectionTitle}</h2>
          <div className="flex flex-wrap justify-center gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="w-[360px] rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden animate-pulse"
                style={{ backgroundColor: 'var(--bg-primary)' }}
              >
                <div className="h-48 bg-gray-200" />
                <div className="p-6 space-y-4">
                  <div className="h-4 bg-gray-200 rounded w-1/4" />
                  <div className="h-6 bg-gray-200 rounded w-3/4" />
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded" />
                    <div className="h-4 bg-gray-200 rounded" />
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section id="news" className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[var(--primary-color)] mb-8 text-center">{sectionTitle}</h2>
          <div className={
            isSingle
              ? "flex justify-center"
              : itemCount === 2
                ? "flex justify-center gap-8"
                : itemCount === 3
                  ? "flex justify-center gap-8"
                  : "flex flex-wrap justify-center gap-8"
          }>
            {displayedNews.map((item) => (
              <article 
                key={item.id} 
                className={
                  (isSingle
                    ? "max-w-[360px] w-full"
                    : itemCount === 2
                      ? "w-[360px]"
                      : itemCount === 3
                      ? "w-[360px]"
                      : "w-[360px]") +
                  " rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden transform transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:ring-2 hover:ring-gray-300 dark:hover:ring-gray-600 cursor-pointer fade-in-up"
                }
                style={{ backgroundColor: 'var(--bg-primary)' }}
                onClick={() => setSelectedArticle(item)}
              >
                <div className="relative">
                  <img 
                    src={item.image_url} 
                    alt={item.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                    {(parseMySqlTimestampToDate(item.created_at) ?? new Date()).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Europe/Berlin' })}
                    <span className="mx-2">•</span>
                    {computeReadingTimeMinutes(item.content)} Min. Lesezeit
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                    {(parseMySqlTimestampToDate(item.created_at) ?? new Date()).toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin' })}
                  </p>
                  <h3 className="text-xl font-semibold mb-2 text-[var(--primary-color)]">{item.title}</h3>
                  <p className="mb-4" style={{ color: 'var(--text-primary)' }}>
                    {item.content.length > 150 
                      ? `${item.content.substring(0, 150)}...` 
                      : item.content}
                  </p>
                  <button className="text-[var(--primary-color)] font-semibold hover:opacity-80">
                    Weiterlesen →
                  </button>
                </div>
              </article>
            ))}
          </div>
          
          {news.length > visibleCount && (
            <div className="text-center mt-8">
              <button
                onClick={() => setVisibleCount((prev) => Math.min(prev + 6, news.length))}
                className="bg-[var(--primary-color)] text-white px-6 py-2 rounded-md hover:opacity-90 transition-opacity"
              >
                Mehr
              </button>
            </div>
          )}
        </div>
      </section>

      {selectedArticle && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--bg-primary)' }}>
            <div className="relative">
              <button 
                onClick={() => setSelectedArticle(null)}
                className="absolute top-4 right-4 rounded-full p-2 shadow-md hover:opacity-80 transition-opacity"
                style={{ backgroundColor: 'var(--bg-secondary)' }}
              >
                <X size={24} style={{ color: 'var(--text-primary)' }} />
              </button>
              <img 
                src={selectedArticle.image_url} 
                alt={selectedArticle.title}
                className="w-full h-[40vh] object-cover"
              />
            </div>
            <div className="p-8">
              <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                {new Date(selectedArticle.created_at).toLocaleDateString('de-DE')}
              </p>
              <h2 className="text-3xl font-bold text-[var(--primary-color)] mb-4">
                {selectedArticle.title}
              </h2>
              <div className="prose max-w-none" style={{ color: 'var(--text-primary)' }}>
                {selectedArticle.content.split('\n').map((paragraph, index) => (
                  <p key={index} className="mb-4">{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NewsSection;

